/**
 * Ações (§2.4.3, §5.5.10; FE §6.5): a ÚNICA API de escrita para `features/` (componentes nunca importam `router/`
 * nem `data/`). Fora dos escritores de §2.4.1 (roteador, state/effects.ts, useKeyboardModality), só estas funções
 * escrevem `route` (via roteador), `timeline` e `ui`. Toda escrita substitui o objeto (DE-B04).
 *
 * Portas injetáveis (o App/main.tsx as liga no boot):
 * - `bindRouter(port)`: `navigate` é de router/navigate.ts (§5.3.1); como `router/` nasce em paralelo e não importa
 *   este arquivo, a ligação é por porta (decisão do núcleo da Fase 2). `navigate` daqui é a reexportação de §2.4.3.
 * - `attachFeedClient(fc)`: porta do FeedClient (DE-B05).
 * A ação `loadGeo` chama `data/loadGeo.ts` direto (DE-B16): a malha é imutável e não depende da fonte.
 * Em TV, as ações de navegação retornam sem efeito (§5.5.9).
 */
import { batch, signal } from '@preact/signals';
import type { FeatureFlags } from '../config';
import type { CandidateNumber, IbgeCode, Office, PlaceCode, TseCode, UfCode } from '../contracts/base';
import type { CommentaryIndex } from '../contracts/commentary';
import type { Insight, InsightIndex } from '../contracts/insights';
import type { StateDetail, ZoneResults } from '../contracts/municipal';
import type { Geography } from '../contracts/static';
import { t } from '../copy/strings';
import type { InsightValidator } from '../data/feedClient';
import { loadGeo as fetchGeo } from '../data/loadGeo';
import { focusUf } from '../model/scene';
import type { MapLink, MapMode, PlaceRoute, ResultsView, RouteOffice, RouteState } from '../model/types';
import {
  clearMyCity,
  readMyCity,
  readOneKeyShortcuts,
  recordTvReload,
  writeMyCity,
  writeOneKeyShortcuts,
} from './storage';
import { config, layout, myCity, route, statics, timeline, ui, view } from './store';

// ── Porta do roteador ────────────────────────────────────────────────────────────────────────────────────────
export interface NavigateOptions {
  /** true = history.replaceState (sem entrada nova); padrão: push (§5.3.1). */
  replace?: boolean;
  /** Roda no fim de afterRouteChange (passo 9 de §5.1); usado pela busca para abrir o perfil (§5.8.5). */
  then?: () => void;
}
export type NavigateFn = (partial: Partial<RouteState>, opts?: NavigateOptions) => void;

/** O que o roteador (router/navigate.ts) entrega às ações. Ligado por main.tsx/App com bindRouter(). */
export interface RouterPort {
  /** navigate de §5.3.1: mescla, normaliza, escreve o hash e roda afterRouteChange. */
  navigate: NavigateFn;
  /** Rota em vigor: a do push em voo (hashchange pendente), senão `route`. Ausente nas portas falsas dos testes. */
  currentRoute?(): RouteState;
  /** Hash canônico da rota atual, com "#": `formatRoute(route.value, ctx.value)` (§5.2.4; nunca tem `tv`). */
  canonicalHash(): string;
  /** Saída da TV: grava `formatRoute(route)` com replaceState, sem `tv=1` (§5.2.7). */
  dropTvHash(): void;
  /** Antes da recarga automática da TV: garante `tv=1` no hash com replaceState (§12.8, DE-20). */
  ensureTvHash(): void;
}

let router: RouterPort | null = null;

/** Liga (ou desliga, com null) a porta do roteador. */
export function bindRouter(port: RouterPort | null): void {
  router = port;
}

/** Rota de onde as ações partem: a do push em voo, se houver; `route` atrasa até o hashchange (teclas "2" e "1"). */
function routeNow(): RouteState {
  return router?.currentRoute?.() ?? route.value;
}

/** Rota em vigor (a do push em voo, senão `route`), para quem decide pela rota antes do hashchange: a cascata do Esc. */
export function currentRoute(): RouteState {
  return routeNow();
}

/** §2.4.3 / §5.3.1. Sem roteador ligado, não faz nada (testes de unidade e boot). */
export function navigate(partial: Partial<RouteState>, opts: NavigateOptions = {}): void {
  if (ui.value.tv.on) return;
  if (router) router.navigate(partial, opts);
  else if (import.meta.env.DEV) console.warn('[actions] navigate sem roteador ligado', partial);
}

// ── Lugares (§5.5.10) ────────────────────────────────────────────────────────────────────────────────────────
/** UFs em ordem alfabética do NOME (§8.2, AC-5.5-04): stepUf circula nesta ordem. */
export const UF_BY_NAME: readonly UfCode[] = Object.freeze([
  'AC', // Acre
  'AL', // Alagoas
  'AP', // Amapá
  'AM', // Amazonas
  'BA', // Bahia
  'CE', // Ceará
  'DF', // Distrito Federal
  'ES', // Espírito Santo
  'GO', // Goiás
  'MA', // Maranhão
  'MT', // Mato Grosso
  'MS', // Mato Grosso do Sul
  'MG', // Minas Gerais
  'PA', // Pará
  'PB', // Paraíba
  'PR', // Paraná
  'PE', // Pernambuco
  'PI', // Piauí
  'RJ', // Rio de Janeiro
  'RN', // Rio Grande do Norte
  'RS', // Rio Grande do Sul
  'RO', // Rondônia
  'RR', // Roraima
  'SC', // Santa Catarina
  'SP', // São Paulo
  'SE', // Sergipe
  'TO', // Tocantins
] as const);

/** Os dois primeiros dígitos do código IBGE identificam a UF (reserva quando a Geography ainda não chegou). */
const IBGE_UF_PREFIX: Readonly<Record<string, UfCode>> = Object.freeze({
  '11': 'RO',
  '12': 'AC',
  '13': 'AM',
  '14': 'RR',
  '15': 'PA',
  '16': 'AP',
  '17': 'TO',
  '21': 'MA',
  '22': 'PI',
  '23': 'CE',
  '24': 'RN',
  '25': 'PB',
  '26': 'PE',
  '27': 'AL',
  '28': 'SE',
  '29': 'BA',
  '31': 'MG',
  '32': 'ES',
  '33': 'RJ',
  '35': 'SP',
  '41': 'PR',
  '42': 'SC',
  '43': 'RS',
  '50': 'MS',
  '51': 'MT',
  '52': 'GO',
  '53': 'DF',
});

const ABROAD_KINDS: ReadonlySet<PlaceRoute['kind']> = new Set(['abroad', 'country', 'abroad_city']);
const PHONE_MODES: ReadonlySet<string> = new Set(['flow', 'tablet', 'land']);

/** UF de um município: pela Geography quando houver; senão pelo prefixo do IBGE. */
export function ufOfMunicipality(ibge: IbgeCode, geo: Geography | null): UfCode | null {
  const hit = geo?.municipalities.find((m) => m.ibge === ibge);
  if (hit) return hit.uf;
  return /^\d{7}$/.test(ibge) ? (IBGE_UF_PREFIX[ibge.slice(0, 2)] ?? null) : null;
}

/** Cidades do país no cadastro; null sem Geography. */
function countryCityCount(geo: Geography | null, iso2: string): number | null {
  if (!geo) return null;
  const country = geo.countries.find((c) => c.iso2.toUpperCase() === iso2);
  if (country) return country.n_cities;
  return geo.abroad_cities.filter((c) => c.country_iso2.toUpperCase() === iso2).length;
}

/** §5.5.10: zona → município → UF → Brasil; cidade → país (2+ cidades) ou Exterior; país → Exterior. */
export function parentOf(p: PlaceRoute, geo: Geography | null): PlaceRoute {
  switch (p.kind) {
    case 'municipality':
      return p.zone != null ? { ...p, zone: null } : { kind: 'uf', uf: p.uf };
    case 'uf':
    case 'abroad':
      return { kind: 'br' };
    case 'country':
      return { kind: 'abroad' };
    case 'abroad_city':
      return (countryCityCount(geo, p.iso2) ?? 0) > 1 ? { kind: 'country', iso2: p.iso2 } : { kind: 'abroad' };
    case 'br':
      return p;
  }
}

/** Exterior, país, cidade e zona → Presidente; senão o cargo atual (§5.5.10). */
function officeFor(place: PlaceRoute): RouteOffice {
  if (ABROAD_KINDS.has(place.kind)) return 'president';
  if (place.kind === 'municipality' && place.zone !== null) return 'president';
  return routeNow().office;
}

/** Abre UF, município, Exterior, país ou cidade; Exterior força Presidente (§5.4, §5.5). */
export function openPlace(place: PlaceRoute): void {
  if (ui.value.tv.on) return;
  navigate({ place, office: officeFor(place) });
}

/** Fecha o lugar e volta ao Brasil do cargo atual (botão ×, §5.5.10). */
export function closePlace(): void {
  if (ui.value.tv.on) return;
  navigate({ place: { kind: 'br' } });
}

/** Sobe um nível (Esc e "‹ {lugar}"); no Brasil, nada (§5.5.10). */
export function upOne(): void {
  if (ui.value.tv.on) return;
  const p = routeNow().place;
  if (p.kind === 'br') return;
  navigate({ place: parentOf(p, statics.value.geography) });
}

/** Mesmo município → UF; outro → município (§5.5.10). */
export function toggleMunicipality(ibge: IbgeCode): void {
  if (ui.value.tv.on) return;
  const p = routeNow().place;
  if (p.kind === 'municipality' && p.ibge === ibge) {
    navigate({ place: { kind: 'uf', uf: p.uf } });
    return;
  }
  const uf = ufOfMunicipality(ibge, statics.value.geography);
  if (!uf) return;
  navigate({ place: { kind: 'municipality', uf, ibge, zone: null } });
}

/** Mesma cidade → raiz do Exterior; outra → cidade (§5.5.10). */
export function toggleAbroadCity(tse: TseCode): void {
  if (ui.value.tv.on) return;
  const p = routeNow().place;
  if (p.kind === 'abroad_city' && p.tse === tse) {
    openPlace({ kind: 'abroad' });
    return;
  }
  const city = statics.value.geography?.abroad_cities.find((c) => c.tse === tse);
  if (!city) return;
  openPlace({ kind: 'abroad_city', iso2: city.country_iso2.toUpperCase(), tse });
}

/** UF anterior/próxima em ordem alfabética do nome, circular (§8.2). Sem UF em foco, nada. */
export function stepUf(delta: 1 | -1): void {
  if (ui.value.tv.on) return;
  const uf = focusUf(routeNow());
  if (!uf) return;
  const i = UF_BY_NAME.indexOf(uf);
  const next = UF_BY_NAME[(i + delta + UF_BY_NAME.length) % UF_BY_NAME.length];
  if (next) navigate({ place: { kind: 'uf', uf: next } });
}

/** Lugar que sobra ao trocar de cargo (tabela de §5.4.2). */
export function placeForOffice(
  place: PlaceRoute,
  to: RouteOffice,
  flags: Pick<FeatureFlags, 'governor_municipal'>,
): PlaceRoute {
  if (to !== 'president' && ABROAD_KINDS.has(place.kind)) return { kind: 'br' };
  if (place.kind === 'municipality') {
    if (to === 'president') return place;
    if (to === 'governor' && flags.governor_municipal) return { ...place, zone: null };
    return { kind: 'uf', uf: place.uf };
  }
  return place;
}

/** Troca o cargo mantendo UF e município (§5.4.2); a memória do modo do mapa fica em `route` (§5.4.3). */
export function setOffice(office: RouteOffice): void {
  if (ui.value.tv.on) return;
  const r = routeNow();
  if (office === r.office) return;
  navigate({ office, place: placeForOffice(r.place, office, config.value.features) });
}

/**
 * Troca entre a Apuração (null) e a tela de comentários (ORD-F-026; docs/TELA-COMENTARIOS.md §1): push (Voltar do
 * navegador volta), mantendo cargo, lugar e momento. Onde a tela não existe, não faz nada. Devolve true se trocou.
 */
export function setScreen(screen: 'comments' | null): boolean {
  const f = config.value.features;
  if (ui.value.tv.on || !(f.replay_commentary || (f.commentary_timeline && f.commentary))) return false;
  if ((routeNow().screen ?? null) === screen) return false;
  navigate({ screen: screen ?? undefined });
  return true;
}

/** Modo do mapa escolhido pelo usuário (push, §9.4). */
export function setMapMode(mode: MapMode): void {
  navigate({ mapMode: mode });
}

/** Modo Candidato com o candidato escolhido (push, §9.3). */
export function setMapCandidate(number: CandidateNumber): void {
  navigate({ mapMode: 'candidate', mapCandidate: number });
}

// ── Perfil de candidato (§8.6, §5.3.2) ───────────────────────────────────────────────────────────────────────
/** Modo do mapa antes de o perfil nacional ligar o modo Candidato (restaurado ao fechar). */
let profileMapMemory: { mapMode: MapMode; mapCandidate: CandidateNumber | null } | null = null;

function nationalLive(r: RouteState): boolean {
  return r.office === 'president' && r.place.kind === 'br' && timeline.value.live;
}

/**
 * Abre o perfil (fora da URL). Presidente na vista nacional e ao vivo: liga o modo Candidato com replace e guarda o
 * modo anterior (§5.3.2). Quem também muda de lugar (busca, §5.8.5) navega antes e chama esta ação no `then`.
 */
export function openProfile(number: CandidateNumber, office: Office, origin: 'list' | 'search'): void {
  // a rota em vigor (o push em voo, se houver): × e logo um clique no herói partem do Brasil, não da UF que saía
  const r = routeNow();
  if (ui.value.profile === null) profileMapMemory = null;
  if (office === 'president' && nationalLive(r) && !(r.mapMode === 'candidate' && r.mapCandidate === number)) {
    if (profileMapMemory === null) profileMapMemory = { mapMode: r.mapMode, mapCandidate: r.mapCandidate };
    navigate({ mapMode: 'candidate', mapCandidate: number }, { replace: true });
  }
  ui.value = { ...ui.value, profile: { number, office, origin } };
}

/** Fecha o perfil e, se ele tinha ligado o modo Candidato, restaura o modo anterior com replace (§8.6). */
export function closeProfile(): void {
  const profile = ui.value.profile;
  if (!profile) return;
  const memory = profileMapMemory;
  profileMapMemory = null;
  ui.value = { ...ui.value, profile: null };
  const r = routeNow();
  if (memory && nationalLive(r) && r.mapMode === 'candidate' && r.mapCandidate === profile.number) {
    navigate({ mapMode: memory.mapMode, mapCandidate: memory.mapCandidate }, { replace: true });
  }
}

// ── Tempo (§10.5) ────────────────────────────────────────────────────────────────────────────────────────────
let replayToken = 0;
let replayAbort: AbortController | null = null;

/**
 * Replay: `timeline.live = false`, `displayTMin`, `replayLoading`; a vista exibida vira `replayView` enquanto o
 * instante pedido carrega (view nunca null depois do 1º snapshot, AC-2.4-04); `t=HHMM` com replace (M18).
 */
export function scrubTo(tMin: number): void {
  const shown = view.value;
  batch(() => {
    timeline.value = { ...timeline.value, live: false, displayTMin: tMin, replayView: shown, replayLoading: true };
    if (ui.value.toast !== null) ui.value = { ...ui.value, toast: null };
  });
  navigate({ replayTMin: tMin }, { replace: true });
  // Busca pelo FeedClient.at() da porta (DE-B05); a Fase 8b confere com §10.5.2 (E_NO_RECORD, aviso e volta).
  const fc = feedClient;
  if (!fc) return;
  replayAbort?.abort();
  const ac = new AbortController();
  replayAbort = ac;
  const token = ++replayToken;
  fc.at(tMin, ac.signal).then(
    (res) => {
      if (token !== replayToken || timeline.value.live) return;
      timeline.value = { ...timeline.value, replayView: res.view, displayTMin: res.shownTMin, replayLoading: false };
    },
    () => {
      if (token !== replayToken || timeline.value.live) return;
      timeline.value = { ...timeline.value, replayLoading: false };
    },
  );
}

/** Volta ao vivo: `timeline.live = true`, `replayView = null`; remove `t` com replace (§10.5). */
export function goLive(): void {
  replayAbort?.abort();
  replayAbort = null;
  replayToken++;
  const t0 = timeline.value;
  if (!t0.live || t0.replayView !== null || t0.replayLoading) {
    timeline.value = { ...t0, live: true, replayView: null, replayLoading: false, displayTMin: t0.clockTMin };
  }
  if (route.value.replayTMin !== null) navigate({ replayTMin: null }, { replace: true });
}

// ── UI efêmera ───────────────────────────────────────────────────────────────────────────────────────────────
// CASCA: Fase 11 — sem a SearchPalette, abrir a busca não faz nada: evita o estado fantasma (aria-expanded="true" sem
// diálogo e atalhos de uma tecla bloqueados até o Esc) achado na revisão da Fase 2. A Fase 11 liga a paleta com
// setSearchPaletteReady(true) na montagem e apaga esta guarda.
let searchPaletteReady = false;
export function setSearchPaletteReady(ready: boolean): void {
  searchPaletteReady = ready;
}

/** Abre/fecha a busca (§5.8); na TV não abre. */
export function toggleSearch(open?: boolean): void {
  const u = ui.value;
  const next = open ?? !u.searchOpen;
  if (next && (u.tv.on || !searchPaletteReady)) return;
  if (next !== u.searchOpen) ui.value = { ...u, searchOpen: next };
}

/** `ui.aboutOpen = variant` (§12.6); o AboutDialog não existe na TV. */
export function openAbout(variant: 'about' | 'simulation'): void {
  const u = ui.value;
  if (u.tv.on || u.aboutOpen === variant) return;
  ui.value = { ...u, aboutOpen: variant };
}

/** Fecha o AboutDialog (Esc, ×, clique fora). */
export function closeAbout(): void {
  if (ui.value.aboutOpen !== null) ui.value = { ...ui.value, aboutOpen: null };
}

/** Botões "Sobre" e "Simulação": um 2º clique fecha (§6.9). */
export function toggleAbout(variant: 'about' | 'simulation'): void {
  if (ui.value.aboutOpen === variant) closeAbout();
  else openAbout(variant);
}

/** CandidatePicker (§9.3; DE-B04). */
export function togglePicker(open?: boolean): void {
  const u = ui.value;
  const next = open ?? !u.pickerOpen;
  if (next && u.tv.on) return;
  if (next !== u.pickerOpen) ui.value = { ...u, pickerOpen: next };
}

/** Lista expandida (§8.5; DE-B04). */
export function setExpanded(id: string | null): void {
  if (ui.value.expanded !== id) ui.value = { ...ui.value, expanded: id };
}

/** Cena atual da TV, escrita pelo TvMode (§12.2; DE-B04). */
export function setTvScene(id: string | null): void {
  const u = ui.value;
  if (u.tv.sceneId !== id) ui.value = { ...u, tv: { ...u.tv, sceneId: id } };
}

/** Aviso transitório padrão (§6.10). */
export const NOTICE_MS = 5000;
let noticeTimer: ReturnType<typeof setTimeout> | null = null;

/** `ui.notice` por `ms` (padrão 5 s); um novo substitui o anterior e reinicia o tempo (§6.10). */
export function showNotice(text: string, ms: number = NOTICE_MS): void {
  if (noticeTimer !== null) clearTimeout(noticeTimer);
  ui.value = { ...ui.value, notice: text };
  noticeTimer = setTimeout(() => {
    noticeTimer = null;
    if (ui.value.notice === text) ui.value = { ...ui.value, notice: null };
  }, ms);
}

// ── Destaque no mapa (§9.11.4) ───────────────────────────────────────────────────────────────────────────────
let mapLinkSource: string | null = null;

function sameMapLink(a: MapLink | null, b: MapLink | null): boolean {
  if (a === null || b === null) return a === b;
  if (a.kind === 'place' && b.kind === 'place') return a.code === b.code;
  if (a.kind === 'zone' && b.kind === 'zone') return a.ibge === b.ibge && a.zone === b.zone;
  return false;
}

/** Destaca sem navegar; `source` identifica quem pediu (só ele limpa). Ignorado na TV. */
export function setMapLink(link: MapLink, source: string): void {
  if (ui.value.tv.on) return;
  mapLinkSource = source;
  if (!sameMapLink(ui.value.mapLink, link)) ui.value = { ...ui.value, mapLink: link };
}

/** Limpa o destaque se ele veio de `source` (pointerleave/blur na origem). */
export function clearMapLink(source: string): void {
  if (mapLinkSource !== source) return;
  mapLinkSource = null;
  if (ui.value.mapLink !== null) ui.value = { ...ui.value, mapLink: null };
}

// ── TV (§12.1) ───────────────────────────────────────────────────────────────────────────────────────────────
interface WakeLockSentinelLike {
  release(): Promise<void>;
}
interface FullscreenDocument {
  fullscreenElement?: Element | null;
  webkitFullscreenElement?: Element | null;
  exitFullscreen?: () => Promise<void>;
  webkitExitFullscreen?: () => void;
}
interface FullscreenElement {
  requestFullscreen?: (opts?: { navigationUI?: 'hide' | 'show' | 'auto' }) => Promise<void>;
  webkitRequestFullscreen?: () => void;
}

let wakeLock: WakeLockSentinelLike | null = null;

function requestFullscreen(): void {
  try {
    const el = document.documentElement as unknown as FullscreenElement;
    if (typeof el.requestFullscreen === 'function') el.requestFullscreen({ navigationUI: 'hide' }).catch(() => {});
    else el.webkitRequestFullscreen?.();
  } catch {
    // falha ignorada (§12.1 passo 5)
  }
}

function leaveFullscreen(): void {
  try {
    const doc = document as unknown as FullscreenDocument;
    if (doc.fullscreenElement && typeof doc.exitFullscreen === 'function') doc.exitFullscreen().catch(() => {});
    else if (doc.webkitFullscreenElement) doc.webkitExitFullscreen?.();
  } catch {
    // falha ignorada
  }
}

/** Pede (de novo) o Wake Lock da TV; o TvMode chama ao voltar de aba oculta (§10.9). Falha ignorada. */
export function reacquireWakeLock(): void {
  if (!ui.value.tv.on || typeof navigator === 'undefined') return;
  const wl = (navigator as { wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinelLike> } }).wakeLock;
  if (!wl) return;
  wl.request('screen').then(
    (sentinel) => {
      if (ui.value.tv.on) wakeLock = sentinel;
      else sentinel.release().catch(() => {});
    },
    () => {},
  );
}

/**
 * Liga a TV (§12.1): volta ao vivo, fecha busca/Sobre/seletor/perfil/listas, `ui.tv.on`, tela cheia (síncrona no
 * clique; `fullscreen: false` na entrada por `?tv=1`, que não tem gesto) e Wake Lock. Não escreve `tv=1` (§5.2.7).
 * Sem efeito com `features.tv = false` ou em modo de celular (§4.1).
 */
export function enterTv(opts: { fullscreen?: boolean } = {}): void {
  if (ui.value.tv.on || !config.value.features.tv || PHONE_MODES.has(layout.value.mode)) return;
  goLive();
  closeProfile();
  ui.value = {
    ...ui.value,
    searchOpen: false,
    aboutOpen: null,
    pickerOpen: false,
    profile: null,
    expanded: null,
    mapLink: null,
    tv: { on: true, sceneId: null, overlay: null },
  };
  if (opts.fullscreen !== false) requestFullscreen();
  reacquireWakeLock();
}

/** Desliga a TV: sai da tela cheia, libera o Wake Lock e remove `tv=1` com replace (§12.1, §5.2.7). */
export function exitTv(): void {
  if (!ui.value.tv.on) return;
  ui.value = { ...ui.value, tv: { on: false, sceneId: null, overlay: null } };
  leaveFullscreen();
  const sentinel = wakeLock;
  wakeLock = null;
  sentinel?.release().catch(() => {});
  router?.dropTvHash();
}

/**
 * Recarga automática da TV depois de erro na raiz (§12.8, F130): até 3 em 10 min. Garante `tv=1` no hash e recarrega;
 * devolve false quando o limite estourou ou o armazenamento falhou (a RootErrorBoundary então só reseta).
 */
export function reloadTv(): boolean {
  if (!recordTvReload(Date.now())) return false;
  router?.ensureTvHash();
  location.reload();
  return true;
}

// ── Atalhos de uma tecla (§15.2.7, X1) ───────────────────────────────────────────────────────────────────────
/**
 * Versão da chave X1. O valor mora no `sessionStorage` (§2.4.4, fonte da verdade); este contador só existe para que
 * quem lê a chave no render (abas e SearchButton, que tiram `aria-keyshortcuts` com a chave desligada) seja
 * re-renderizado quando ela muda. Sem ele, as abas mantinham "1"/"2" até a próxima recarga.
 */
const oneKeyVersion = signal(0);

/** Estado atual da chave (ligado por padrão). Lido no render, inscreve o componente nas trocas da chave. */
export function getOneKeyShortcuts(): boolean {
  void oneKeyVersion.value;
  return readOneKeyShortcuts();
}

/** Liga/desliga os atalhos de uma tecla (chave do AboutDialog). */
export function setOneKeyShortcuts(on: boolean): void {
  writeOneKeyShortcuts(on);
  oneKeyVersion.value++;
}

// ── Compartilhar (§6.7) ──────────────────────────────────────────────────────────────────────────────────────
let sharing = false;

/** Hash sem `tv` (reserva quando o roteador não está ligado). */
function hashWithoutTv(hash: string): string {
  const q = hash.indexOf('?');
  if (q < 0) return hash;
  const params = new URLSearchParams(hash.slice(q + 1));
  params.delete('tv');
  const rest = params.toString();
  return rest ? `${hash.slice(0, q)}?${rest}` : hash.slice(0, q);
}

/**
 * Compartilha a vista (§6.7): `origin + pathname + formatRoute(route)` (sem query de fonte, sem `tv`), título da aba.
 * navigator.share sem await anterior (ativação do usuário); AbortError não avisa; senão copia e avisa (5 s / 15 s).
 * Com `opts` (ORD-F2-022 item 1.6, botão Compartilhar da FRONT-2): `text(url)` monta o texto pronto e `file` é a imagem
 * da D5, preparada ANTES do toque (o Safari perde a ativação se o toque esperar um desenho). A imagem só vai se
 * `navigator.canShare({ files })`; se o envio com ela falhar, recua para o texto e o link; por fim, copia o link.
 * Sem `opts`, o comportamento de sempre.
 */
export async function share(opts?: { text?: (url: string) => string; file?: File | null }): Promise<void> {
  if (sharing) return;
  sharing = true;
  try {
    const hash = router ? router.canonicalHash() : hashWithoutTv(location.hash);
    const url = location.origin + location.pathname + hash;
    const title = document.title;
    const text = opts?.text?.(url);
    if (typeof navigator.share === 'function') {
      const base: ShareData = text ? { title, text, url } : { title, url };
      const file = opts?.file ?? null;
      const withFile: ShareData | null =
        file && typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })
          ? { ...base, files: [file] }
          : null;
      for (const data of withFile ? [withFile, base] : [base]) {
        try {
          await navigator.share(data);
          return;
        } catch (e) {
          if ((e as { name?: string } | null)?.name === 'AbortError') return;
        }
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      showNotice(t('share.copied'), 5000);
    } catch {
      showNotice(t('share.fallback', { URL: url }), 15000);
    }
  } finally {
    sharing = false;
  }
}

// ── Porta do FeedClient (DE-B05) ─────────────────────────────────────────────────────────────────────────────
/** Resultado de FeedClient.at() (§14.8.3). */
export interface ReplayResultLike {
  view: ResultsView;
  requestedTMin: number;
  shownTMin: number;
  seq: number;
}
/** Os métodos do FeedClient (§14.8.3) que as ações reexpõem; o FeedClient real é atribuível. */
export interface FeedPort {
  refreshNow(): Promise<void>;
  at(tMin: number, signal?: AbortSignal): Promise<ReplayResultLike>;
  stateDetail(place: PlaceCode, office?: Office): Promise<StateDetail | null>;
  zones(ibge: IbgeCode): Promise<ZoneResults | null>;
  insightIndex(validate: InsightValidator<InsightIndex>): Promise<InsightIndex | null>;
  insight(path: string, validate: InsightValidator<Insight>): Promise<Insight | null>;
  /** Ausente no replay (comentários nos replays: depois de 25/10, §8 de 15/10, item 3). */
  commentaryIndex?(validate: InsightValidator<CommentaryIndex>): Promise<CommentaryIndex | null>;
  commentaryFile?<T>(path: string, validate: InsightValidator<T>): Promise<T | null>;
  wantMunicipal(on: boolean): void;
  wantProbabilityDetail(on: boolean): void;
}

// A porta é ligada por app/feedBinding.ts a cada FeedClient criado; sem cliente (testes, antes do boot) os métodos
// devolvem "sem dado" e os pedidos de camada ficam guardados para a ligação.
let feedClient: FeedPort | null = null;
let wantMun = false;
let wantProb = false;

/** Chamada por app/feedBinding.ts a cada FeedClient criado (boot e cada recriar() do DevPanel). */
export function attachFeedClient(fc: FeedPort | null): void {
  feedClient = fc;
  if (fc) {
    fc.wantMunicipal(wantMun);
    fc.wantProbabilityDetail(wantProb);
  }
}

let chunkFailureHandler: (() => void) | null = null;

/**
 * Um pedaço sob demanda falhou (pré-mortem de 25/10, ORD-F-146): a recuperação do build (app/staleBuild.ts, que se liga
 * por `bindChunkFailure` na instalação) confere o build no ar. Sem ela ligada, nada.
 */
export function chunkFailed(): void {
  chunkFailureHandler?.();
}

/** (app/staleBuild.ts) */
export function bindChunkFailure(fn: (() => void) | null): void {
  chunkFailureHandler = fn;
}

/** "Tentar de novo"; volta de visibilidade, online e bfcache. */
export function refreshNow(): Promise<void> {
  return feedClient ? feedClient.refreshNow() : Promise.resolve();
}

/** Detalhe de UF ou Exterior sob demanda (cache 45 s). */
export function stateDetail(place: PlaceCode, office?: Office): Promise<StateDetail | null> {
  return feedClient ? feedClient.stateDetail(place, office) : Promise.resolve(null);
}

/** Zonas de um município (P2). */
export function zones(ibge: IbgeCode): Promise<ZoneResults | null> {
  return feedClient ? feedClient.zones(ibge) : Promise.resolve(null);
}

/** Índice de Situação incomum do quadro atual (P-27); o validador vem de quem pede (fora do JS crítico). */
export function insightIndex(validate: InsightValidator<InsightIndex>): Promise<InsightIndex | null> {
  return feedClient ? feedClient.insightIndex(validate) : Promise.resolve(null);
}

/** Comentários ao vivo (P-32): o índice do snapshot atual, com o validador do chunk do painel. */
export function commentaryIndex(validate: InsightValidator<CommentaryIndex>): Promise<CommentaryIndex | null> {
  return feedClient?.commentaryIndex ? feedClient.commentaryIndex(validate) : Promise.resolve(null);
}

/** P-39: um arquivo imutável das horas fechadas dos comentários, com o validador do chunk do painel. */
export function commentaryFile<T>(path: string, validate: InsightValidator<T>): Promise<T | null> {
  return feedClient?.commentaryFile ? feedClient.commentaryFile(path, validate) : Promise.resolve(null);
}

/** Pacote de evidências de um sinal (`path` do índice). */
export function insight(path: string, validate: InsightValidator<Insight>): Promise<Insight | null> {
  return feedClient ? feedClient.insight(path, validate) : Promise.resolve(null);
}

/** O mapa declara que precisa da camada municipal (M2). */
export function wantMunicipal(on: boolean): void {
  wantMun = on;
  feedClient?.wantMunicipal(on);
}

/** ProbabilitySlot visível. */
export function wantProbabilityDetail(on: boolean): void {
  wantProb = on;
  feedClient?.wantProbabilityDetail(on);
}

/** Malhas de /geo (§9.2; DE-B16). */
export type GeoName = 'br-uf' | 'br-mun' | 'world' | `zones/${IbgeCode}`;

/**
 * Busca uma malha de /geo pelo nome (DE-B16): `data/loadGeo.ts`, com o caminho do manifesto, a prioridade de §15.7.4
 * e as 3 tentativas de §9.2.7. Devolve o TopoJSON bruto (o `GeoStore` valida); rejeita com `GeoLoadError`.
 */
export function loadGeo(name: GeoName, signal?: AbortSignal): Promise<unknown> {
  return fetchGeo(name, signal);
}

// ── Município guardado no aparelho (D3; ORD-F2-022 PR "cidade"; SINAL-F2-013) ─────────────────────────────────────
/** Lê o código guardado (ao montar quem o usa), só com `features.my_city`. */
export function loadMyCity(): void {
  if (!config.value.features.my_city) return;
  myCity.value = readMyCity();
}

/** Guarda o município escolhido (só o código IBGE, neste aparelho). Sem a chave, não faz nada. */
export function chooseMyCity(ibge: IbgeCode): void {
  if (!config.value.features.my_city) return;
  writeMyCity(ibge);
  myCity.value = ibge;
}

/** "Esquecer": apaga o código do aparelho. */
export function forgetMyCity(): void {
  clearMyCity();
  myCity.value = null;
}
